import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { certifications } from '../../data/constants';

gsap.registerPlugin(ScrollTrigger);

/* ══════════════════════════════════════════════════
   STYLES
══════════════════════════════════════════════════ */

const Section = styled.section`
  padding: 100px 0;
  background: ${({ theme }) => theme.bg};
  overflow: hidden;
  position: relative;

  /* subtle dot grid */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(34,211,238,0.045) 1px, transparent 1px);
    background-size: 34px 34px;
    pointer-events: none;
    mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%);
  }
`;

const Wrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 5vw;
`;

const Header = styled.div`
  margin-bottom: 64px;
`;

const Label = styled.p`
  font-family: 'Space Grotesk', monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 10px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(32px, 4vw, 52px);
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  letter-spacing: -0.03em;
  line-height: 1;
`;

const CertCount = styled.span`
  font-family: 'Space Grotesk', monospace;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.text_secondary};
  opacity: 0.4;
  letter-spacing: 0.08em;
`;

/* ── Split body ─────────────────────────────────── */

const Body = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 20px;
  overflow: hidden;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

/* ── Left: list ─────────────────────────────────── */

const List = styled.div`
  border-right: 1px solid ${({ theme }) => theme.border};

  @media (max-width: 768px) {
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.border};
  }
`;

const Row = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 22px 28px;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  transition: background 0.2s;
  overflow: hidden;

  &:last-child { border-bottom: none; }

  /* fill sweep on hover/active */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ $accent }) => $accent
      ? `linear-gradient(90deg, ${$accent}10 0%, transparent 100%)`
      : 'rgba(34,211,238,0.06)'};
    transform: translateX(-101%);
    transition: transform 0.3s ease;
  }

  &:hover::before,
  &.active::before {
    transform: translateX(0);
  }

  /* left accent bar */
  &::after {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: ${({ $accent }) => $accent || '#22D3EE'};
    transform: scaleY(0);
    transform-origin: bottom;
    transition: transform 0.3s ease;
    border-radius: 0 2px 2px 0;
  }

  &:hover::after,
  &.active::after {
    transform: scaleY(1);
  }
`;

const RowLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowNum = styled.span`
  font-family: 'Space Grotesk', monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: ${({ $accent, $active }) => $active ? ($accent || '#22D3EE') : 'rgba(148,163,184,0.4)'};
  display: block;
  margin-bottom: 4px;
  transition: color 0.2s;

  ${Row}:hover & {
    color: ${({ $accent }) => $accent || '#22D3EE'};
  }
`;

const RowTitle = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ $active, theme }) => $active ? theme.text_primary : theme.text_secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s;

  ${Row}:hover & { color: ${({ theme }) => theme.text_primary}; }
`;

const RowSub = styled.p`
  font-size: 11.5px;
  color: ${({ theme }) => theme.text_secondary};
  opacity: 0.5;
  margin-top: 2px;
`;

const RowArrow = styled.span`
  font-size: 15px;
  color: ${({ $accent }) => $accent || '#22D3EE'};
  opacity: 0;
  transition: opacity 0.2s, transform 0.2s;
  flex-shrink: 0;

  ${Row}:hover &, ${Row}.active & {
    opacity: 1;
    transform: translateX(2px);
  }
`;

/* ── Right: detail panel ────────────────────────── */

const Detail = styled.div`
  position: relative;
  background: ${({ theme }) => theme.card};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  overflow: hidden;

  /* accent glow blob */
  &::before {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: ${({ $accent }) => `radial-gradient(circle, ${$accent || '#22D3EE'}18 0%, transparent 70%)`};
    top: -60px;
    right: -60px;
    pointer-events: none;
    transition: background 0.5s;
  }
`;

const DetailInner = styled.div`
  position: relative;
  z-index: 1;
  padding: 48px 44px;
  width: 100%;

  @media (max-width: 768px) { padding: 32px 24px; }
`;

const DetailGhost = styled.div`
  position: absolute;
  bottom: -20px;
  right: -10px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(80px, 12vw, 140px);
  font-weight: 900;
  color: transparent;
  -webkit-text-stroke: 1px ${({ $accent }) => `${$accent || '#22D3EE'}18`};
  letter-spacing: -0.06em;
  line-height: 1;
  user-select: none;
  pointer-events: none;
`;

const DetailLabel = styled.span`
  font-family: 'Space Grotesk', monospace;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ $accent }) => $accent || '#22D3EE'};
  display: block;
  margin-bottom: 16px;
`;

const DetailTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(22px, 3vw, 34px);
  font-weight: 800;
  color: ${({ theme }) => theme.text_primary};
  letter-spacing: -0.03em;
  line-height: 1.15;
  margin-bottom: 8px;
`;

const DetailIssuer = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 6px;
`;

const DetailDate = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.text_secondary};
  opacity: 0.5;
  margin-bottom: 24px;
`;

const AccentLine = styled.div`
  width: 36px;
  height: 2px;
  border-radius: 2px;
  background: ${({ $accent }) => $accent || '#22D3EE'};
  box-shadow: 0 0 8px ${({ $accent }) => `${$accent || '#22D3EE'}88`};
  margin-bottom: 20px;
`;

const SkillsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 28px;
`;

const SkillTag = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 4px 11px;
  border-radius: 100px;
  color: ${({ $accent }) => $accent || '#22D3EE'};
  background: ${({ $accent }) => `${$accent || '#22D3EE'}14`};
  border: 1px solid ${({ $accent }) => `${$accent || '#22D3EE'}28`};
`;

const CredBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.22s;
  background: ${({ $accent }) => $accent || '#22D3EE'};
  color: #07071A;
  box-shadow: 0 0 24px ${({ $accent }) => `${$accent || '#22D3EE'}44`};

  &:hover {
    box-shadow: 0 0 48px ${({ $accent }) => `${$accent || '#22D3EE'}77`};
    transform: translateY(-2px);
  }

  &[data-disabled] {
    background: rgba(255,255,255,0.06);
    color: rgba(255,255,255,0.25);
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const Verified = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 16px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  color: ${({ $accent }) => $accent || '#22D3EE'};
  opacity: 0.65;
  text-transform: uppercase;
`;

/* ══════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════ */

const Certifications = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const sectionRef = useRef(null);
  const listRef    = useRef(null);
  const detailRef  = useRef(null);
  const innerRef   = useRef(null);

  const cert = certifications[activeIdx];

  /* ── Switch active cert with fade ── */
  const handleSwitch = (idx) => {
    if (idx === activeIdx) return;
    gsap.to(innerRef.current, {
      opacity: 0, y: 10, duration: 0.15,
      onComplete: () => {
        setActiveIdx(idx);
        gsap.fromTo(innerRef.current,
          { opacity: 0, y: 10 },
          { opacity: 1, y: 0, duration: 0.28, ease: 'power2.out' }
        );
      },
    });
  };

  /* ── Entrance animation ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.cert-header-el',
        { y: 36, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 85%' } }
      );

      const rows = listRef.current?.querySelectorAll('.cert-row');
      if (rows?.length) {
        gsap.fromTo(rows,
          { x: -28, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.5, stagger: 0.08, ease: 'power3.out',
            scrollTrigger: { trigger: listRef.current, start: 'top 82%' } }
        );
      }

      gsap.fromTo(detailRef.current,
        { x: 40, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.7, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 80%' } }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section id="certifications" ref={sectionRef}>
      <Wrapper>
        <Header>
          <div className="cert-header-el">
            <Label>Verified Skills</Label>
            <TitleRow>
              <Title>Certifications</Title>
              <CertCount>{String(certifications.length).padStart(2, '0')} credentials</CertCount>
            </TitleRow>
          </div>
        </Header>

        <Body>
          {/* ── Left: list ─────────────── */}
          <List ref={listRef}>
            {certifications.map((c, i) => (
              <Row
                key={c.id}
                className={`cert-row${i === activeIdx ? ' active' : ''}`}
                $accent={c.color}
                onMouseEnter={() => handleSwitch(i)}
                onClick={() => handleSwitch(i)}
              >
                <RowLeft>
                  <RowNum $accent={c.color} $active={i === activeIdx}>
                    {String(i + 1).padStart(2, '0')}
                  </RowNum>
                  <RowTitle $active={i === activeIdx}>{c.title}</RowTitle>
                  <RowSub>{c.issuer}{c.date ? ` · ${c.date}` : ''}</RowSub>
                </RowLeft>
                <RowArrow $accent={c.color}>→</RowArrow>
              </Row>
            ))}
          </List>

          {/* ── Right: detail ──────────── */}
          <Detail ref={detailRef} $accent={cert.color}>
            <DetailGhost $accent={cert.color}>
              {String(activeIdx + 1).padStart(2, '0')}
            </DetailGhost>

            <DetailInner ref={innerRef}>
              <DetailLabel $accent={cert.color}>{cert.issuer}</DetailLabel>
              <DetailTitle>{cert.title}</DetailTitle>
              <DetailIssuer>{cert.issuer}</DetailIssuer>
              {cert.date && <DetailDate>Issued {cert.date}</DetailDate>}

              <AccentLine $accent={cert.color} />

              {cert.skills?.length > 0 && (
                <SkillsWrap>
                  {cert.skills.map((s, i) => (
                    <SkillTag key={i} $accent={cert.color}>{s}</SkillTag>
                  ))}
                </SkillsWrap>
              )}

              {cert.link ? (
                <CredBtn
                  href={cert.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  $accent={cert.color}
                >
                  View Credential ↗
                </CredBtn>
              ) : (
                <CredBtn as="span" $accent={cert.color} data-disabled>
                  Link Coming Soon
                </CredBtn>
              )}

              <Verified $accent={cert.color}>
                <span>✓</span> Verified Credential
              </Verified>
            </DetailInner>
          </Detail>
        </Body>
      </Wrapper>
    </Section>
  );
};

export default Certifications;
